import * as React from "react";
import { useCallback, useEffect, useRef, useState } from "react";
import { Loader2 } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";
import { TRADE_LOG_FILTER_INPUT_CLASS } from "@/components/trade-filter-selects";

import type { ReportStrategyPolicyVersion } from "../../../types/reports";
import {
  POLICY_VERSION_TITLE_MAX_LENGTH,
  policyVersionDisplayTitle,
} from "./strategyDetailUtils";

export type PolicyVersionTitleEditorProps = {
  version: ReportStrategyPolicyVersion;
  disabled?: boolean;
  saving?: boolean;
  /** Persist custom title; pass `""` to clear. */
  onSaveTitle: (title: string) => void | Promise<void>;
  className?: string;
  inputClassName?: string;
};

/**
 * Inline rename for a policy / combined policy version.
 * Blur / Enter saves; Escape cancels. Empty draft clears the custom title.
 */
export function PolicyVersionTitleEditor({
  version,
  disabled = false,
  saving = false,
  onSaveTitle,
  className,
  inputClassName,
}: PolicyVersionTitleEditorProps): React.ReactElement {
  const displayTitle = policyVersionDisplayTitle(version);
  const storedTitle = typeof version.title === "string" ? version.title.trim() : "";
  const [focused, setFocused] = useState(false);
  const [draft, setDraft] = useState(storedTitle);
  const rowRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (focused) return;
    setDraft(storedTitle);
  }, [focused, storedTitle, version.id]);

  const commit = useCallback(() => {
    const next = draft.trim().slice(0, POLICY_VERSION_TITLE_MAX_LENGTH);
    if (next === storedTitle) {
      setFocused(false);
      return;
    }
    void Promise.resolve(onSaveTitle(next)).finally(() => setFocused(false));
  }, [draft, onSaveTitle, storedTitle]);

  const onBlur = useCallback((e: React.FocusEvent<HTMLInputElement>) => {
    const next = e.relatedTarget;
    if (next instanceof Node && rowRef.current?.contains(next)) return;
    setFocused(false);
    setDraft(storedTitle);
  }, [storedTitle]);

  const dirty = draft.trim() !== storedTitle;
  const inputMaxWidthCh = POLICY_VERSION_TITLE_MAX_LENGTH + 4;

  return (
    <div
      ref={rowRef}
      data-policy-version-title-editor=""
      className={cn("flex min-w-0 flex-wrap items-center gap-2", className)}
    >
      <Input
        value={focused ? draft : displayTitle}
        disabled={disabled || saving}
        maxLength={POLICY_VERSION_TITLE_MAX_LENGTH}
        placeholder={displayTitle}
        aria-label="Policy name"
        className={cn(
          TRADE_LOG_FILTER_INPUT_CLASS,
          "h-9 min-w-[12rem] flex-1 font-[family-name:var(--font-primary)] text-sm font-medium text-[color:var(--brand-tropical-surf)]",
          inputClassName
        )}
        style={{ maxWidth: `${inputMaxWidthCh}ch` }}
        onFocus={() => {
          setDraft(storedTitle);
          setFocused(true);
        }}
        onChange={(e) => setDraft(e.target.value.slice(0, POLICY_VERSION_TITLE_MAX_LENGTH))}
        onBlur={onBlur}
        onKeyDown={(e) => {
          if (e.key === "Enter") {
            e.preventDefault();
            commit();
          } else if (e.key === "Escape") {
            e.preventDefault();
            setDraft(storedTitle);
            setFocused(false);
          }
        }}
      />
      {focused && dirty ? (
        <Button
          type="button"
          variant="gradient"
          size="sm"
          data-font="primary"
          className="h-8 shrink-0"
          disabled={disabled || saving}
          onMouseDown={(e) => e.preventDefault()}
          onClick={() => commit()}
        >
          {saving ? (
            <>
              <Loader2 className="size-4 shrink-0 animate-spin" aria-hidden />
              Saving…
            </>
          ) : (
            "Save name"
          )}
        </Button>
      ) : null}
    </div>
  );
}
